import { type Scope, createContextScope } from '@radix-ui/react-context'
import { useCallback, useState } from 'react'
import type { PressableProps } from 'react-native'
import { ListItemDescription } from './components/list-item-description'
import { ListItemHeader } from './components/list-item-header'
import { ListItemIcon } from './components/list-item-icon'
import { ListItemTag } from './components/list-item-tag'
import { ListItemTitle } from './components/list-item-title'
import * as Comp from './list-item.styles'
import type { ListItemProps } from './list-item.types'

type ListItemRootProps = ListItemProps &
  PressableProps & {
    children: React.ReactNode
    testID?: string
  }

const DISPLAY_NAME = 'ListItemRoot'

export const ListItemRoot = (props: ListItemRootProps) => {
  const {
    children,
    disabled = false,
    enabledLine,
    onFocus,
    onBlur,
    __scopeListItem,
    testID,
    ...rest
  } = props as ScopedProps<ListItemRootProps>

  const [isPressed, setIsPressed] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handlePress = useCallback((type: 'in' | 'out') => {
    setIsPressed(type === 'in')
  }, [])

  const componentProps = {
    isPressed,
    isFocused,
    disabled,
    testID,
  }
  return (
    <ListItemProvider scope={__scopeListItem} disabled={disabled}>
      <Comp.Root
        {...rest}
        onPressIn={() => handlePress('in')}
        onPressOut={() => handlePress('out')}
        onFocus={event => {
          if (!disabled) {
            setIsFocused(true)
          }
          onFocus?.(event)
        }}
        onBlur={event => {
          if (!disabled) {
            setIsFocused(false)
          }
          onBlur?.(event)
        }}
        {...componentProps}
      >
        {children}
        {enabledLine && <Comp.Line testID="list-item-line" />}
      </Comp.Root>
    </ListItemProvider>
  )
}

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

export type ScopedProps<P> = P & {
  __scopeListItem?: Scope
}

type ListItemContext = ListItemProps

const [createListItemContext] = createContextScope(DISPLAY_NAME)

export const [ListItemProvider, useListItemContext] =
  createListItemContext<ListItemContext>(DISPLAY_NAME)

export const ListItem = {
  Root: ListItemRoot,
  Title: ListItemTitle,
  Description: ListItemDescription,
  Header: ListItemHeader,
  Tag: ListItemTag,
  Icon: ListItemIcon,
}
